<template>
  <section class="bg-gray-50 py-20">
    <div class="container mx-auto px-4">
      <div class="flex flex-col items-center text-center" v-scroll-reveal="{ delay: 0.1 }">
        <p class="text-sm uppercase tracking-[0.6em] text-amber-500">Testimonials</p>
        <h2 class="mt-4 text-4xl font-bold text-gray-900 lg:text-5xl">
          Reviews from Pilgrims Worldwide
        </h2>
        <p class="mt-4 max-w-3xl text-gray-600">
          Hear from families and group leaders who trusted us with their Umrah journeys—from visa support to local tours.
        </p>
      </div>

      <div class="mt-12 grid gap-8 lg:grid-cols-2">
        <!-- Video testimonial -->
        <div class="rounded-[32px] border border-gray-100 bg-white/90 p-6 shadow-lg" v-scroll-reveal="{ delay: 0.2 }">
          <div class="aspect-video overflow-hidden rounded-2xl">
            <iframe
              class="h-full w-full"
              src="https://www.youtube.com/embed/5XCI_gP6m2E"
              title="Umrah Experience"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
            ></iframe>
          </div>
        </div>

        <!-- Reviews Carousel -->
        <div class="relative" v-scroll-reveal="{ delay: 0.2 }">
          <div class="relative overflow-hidden rounded-[32px] border border-gray-100 bg-gradient-to-br from-white to-amber-50/30 shadow-lg">
            <!-- Decorative Background Pattern -->
            <!-- <div class="absolute inset-0 opacity-2">
              <div class="absolute top-10 right-10 text-amber-500 text-9xl font-serif">"</div>
              <div class="absolute bottom-10 left-10 text-amber-500 text-9xl font-serif">"</div>
            </div> -->
            
            <!-- Reviews Container -->
            <div class="relative h-[400px]">
              <transition name="slide" mode="out-in">
                <article
                  :key="currentReviewIndex"
                  class="absolute inset-0 flex flex-col px-10 py-8 sm:px-12"
                >
                  <!-- Reviewer Info at Top -->
                  <div class="flex items-center gap-4 mb-6 pb-6 border-b border-gray-200">
                    <img 
                      :src="reviews[currentReviewIndex].avatar" 
                      :alt="reviews[currentReviewIndex].name" 
                      class="h-16 w-16 rounded-full object-cover border-2 border-amber-400/40 shadow-lg"
                    />
                    <div class="flex-1">
                      <p class="font-bold text-gray-900 text-lg mb-1">{{ reviews[currentReviewIndex].name }}</p>
                      <p class="text-sm uppercase tracking-[0.2em] text-amber-500 font-medium mb-2">{{ reviews[currentReviewIndex].location }}</p>
                      <!-- Rating Stars Below -->
                      <div class="flex items-center gap-1">
                        <svg 
                          v-for="i in 5" 
                          :key="i" 
                          class="h-4 w-4" 
                          :class="i <= (reviews[currentReviewIndex].rating || 5) ? 'text-amber-400' : 'text-gray-300'"
                          :fill="i <= (reviews[currentReviewIndex].rating || 5) ? 'currentColor' : 'none'"
                          :stroke="i <= (reviews[currentReviewIndex].rating || 5) ? 'currentColor' : 'currentColor'"
                          stroke-width="1"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.97a1 1 0 00.95.69h4.178c.969 0 1.371 1.24.588 1.81l-3.384 2.459a1 1 0 00-.364 1.118l1.286 3.97c.3.921-.755 1.688-1.54 1.118l-3.384-2.46a1 1 0 00-1.176 0l-3.384 2.46c-.785.57-1.84-.197-1.54-1.118l1.286-3.97a1 1 0 00-.364-1.118L2.045 9.397c-.783-.57-.38-1.81.588-1.81h4.178a1 1 0 00.95-.69l1.286-3.97z" />
                        </svg>
                        <span class="text-sm text-gray-600 font-semibold ml-1">{{ reviews[currentReviewIndex].rating }}.0</span>
                      </div>
                    </div>
                  </div>

                  <!-- Quote Icon -->
                  <!-- <div class="mb-3">
                    <svg class="h-8 w-8 text-amber-400/50" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.996 2.151c-2.481.167-4.131 1.143-4.131 3.849 0 1.712.847 2.648 2.328 2.648v7.352h-7.176zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.481.167-4.131 1.143-4.131 3.849 0 1.712.847 2.648 2.328 2.648v7.352h-7.176z"/>
                    </svg>
                  </div> -->

                  <!-- Review Message -->
                  <p class="flex-1 text-base text-gray-700 leading-relaxed mb-6 font-medium">
                    {{ reviews[currentReviewIndex].message }}
                  </p>

                  <!-- Trust Badge -->
                  <div class="flex items-center gap-2 text-xs text-gray-500 pt-4 border-t border-gray-100">
                    <svg class="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                    </svg>
                    <span>Trusted by thousands of pilgrims worldwide</span>
                  </div>
                </article>
              </transition>
            </div>

            <!-- Navigation Arrows -->
            <button
              @click="previousReview"
              class="absolute left-1 top-1/2 -translate-y-1/2 z-10 rounded-full bg-white/90 backdrop-blur-sm p-2 sm:p-2.5 shadow-lg transition hover:bg-white hover:scale-110 border border-gray-200"
              aria-label="Previous review"
            >
              <svg class="h-4 w-4 sm:h-5 sm:w-5 text-gray-700" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              @click="nextReview"
              class="absolute right-1 top-1/2 -translate-y-1/2 z-10 rounded-full bg-white/90 backdrop-blur-sm p-2 sm:p-2.5 shadow-lg transition hover:bg-white hover:scale-110 border border-gray-200"
              aria-label="Next review"
            >
              <svg class="h-4 w-4 sm:h-5 sm:w-5 text-gray-700" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <!-- Dots Indicator -->
            <div class="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              <button
                v-for="(review, index) in reviews"
                :key="index"
                @click="currentReviewIndex = index"
                class="h-2 rounded-full transition-all"
                :class="currentReviewIndex === index ? 'w-8 bg-amber-500' : 'w-2 bg-gray-300'"
                :aria-label="`Go to review ${index + 1}`"
              ></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { listItems } from '../../services/dataStore'

export default {
  name: 'ReviewsSection',
  async mounted() {
    this.reviews = await listItems('reviews', this.reviews)
  },
  data() {
    return {
      currentReviewIndex: 0,
      reviews: [
        {
          name: 'Ayesha Siddiqui',
          location: 'Manchester, UK',
          message: '"Exceptionally responsive team! They managed to book business-class tickets even during the peak Ramadan rush and went the extra mile by arranging a dedicated."',
          avatar: 'https://randomuser.me/api/portraits/women/32.jpg',
          rating: 5
        },
        {
          name: 'Imran Qureshi',
          location: 'Karachi, PK',
          message: '"Our youth group of 18 people had everything arranged—visa, hotels, and a Hunza tour after Umrah. Truly stress-free."',
          avatar: 'https://randomuser.me/api/portraits/men/75.jpg',
          rating: 5
        },
        {
          name: 'Sara Khan',
          location: 'Doha, QA',
          message: '"Loved the concierge service. They surprised my parents with dates & Zamzam gift baskets in the hotel room."',
          avatar: 'https://randomuser.me/api/portraits/women/68.jpg',
          rating: 5
        }
      ]
    }
  },
  methods: {
    nextReview() {
      this.currentReviewIndex = (this.currentReviewIndex + 1) % this.reviews.length
    },
    previousReview() {
      this.currentReviewIndex = (this.currentReviewIndex - 1 + this.reviews.length) % this.reviews.length
    }
  }
}
</script>

<style scoped>
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.slide-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}
</style>
